import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import DateTimeRangeFilterItem from '../../../delivery/components/DateTimeRangeFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface SupportLoginFilterOptions {
    operatorAccountId: string;
    operatorPhone: string;
    targetAccountId: string;
    targetPhone: string;
    createdAtRange: [string | null, string | null];
}

export const DEFAULT_FILTER_OPTIONS: SupportLoginFilterOptions = {
    operatorAccountId: '',
    operatorPhone: '',
    targetAccountId: '',
    targetPhone: '',
    createdAtRange: [null, null],
};

interface SupportLoginFilterProps {
    filters: SupportLoginFilterOptions;
    setFilters: React.Dispatch<React.SetStateAction<SupportLoginFilterOptions>>;
}

const SupportLoginFilter: React.FC<SupportLoginFilterProps> = ({ filters, setFilters }) => {
    const { t } = useTranslation();

    const labelOperatorAccountId = t('supportTools.supportLogin.filters.operatorAccountId');
    const labelOperatorPhone = t('supportTools.supportLogin.filters.operatorPhone');
    const labelTargetAccountId = t('supportTools.supportLogin.filters.targetAccountId');
    const labelTargetPhone = t('supportTools.supportLogin.filters.targetPhone');
    const labelCreatedAt = t('supportTools.supportLogin.filters.createdAt');

    const col1Labels = [labelOperatorAccountId, labelTargetAccountId];
    const col2Labels = [labelOperatorPhone, labelTargetPhone];
    const col3Labels = [labelCreatedAt];

    const handleChange = (field: keyof SupportLoginFilterOptions, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="mb-2 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelOperatorAccountId}
                        value={filters.operatorAccountId}
                        onChange={(e) => handleChange('operatorAccountId', e.target.value)}
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelOperatorPhone}
                        value={filters.operatorPhone}
                        onChange={(e) => handleChange('operatorPhone', e.target.value)}
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <DateTimeRangeFilterItem
                        label={labelCreatedAt}
                        value={filters.createdAtRange}
                        onChange={(dates) => {
                            setFilters(prev => ({ ...prev, createdAtRange: dates ?? [null, null] }));
                        }}
                        spanClassName={getSmartLabelClassName(col3Labels)}
                        format="YYYY/MM/DD HH:mm"
                        showTime={{
                            format: 'HH:mm',
                            defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                        }}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelTargetAccountId}
                        value={filters.targetAccountId}
                        onChange={(e) => handleChange('targetAccountId', e.target.value)}
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelTargetPhone}
                        value={filters.targetPhone}
                        onChange={(e) => handleChange('targetPhone', e.target.value)}
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
            </div>
        </div>
    );
};

export default SupportLoginFilter;
